import React, { useState, useEffect, useMemo } from 'react';
import { Typography } from '@mui/material';
import { Visibility, Reorder } from '@mui/icons-material';
import { useFormik, type FormikErrors } from 'formik';
import { object, array, string } from 'yup';
import dayjs from 'dayjs';
import { prioritizeModeOptions, PRIORITIZE_EDIT_MODE } from './options';
import { MONITOR_LIST_TEXT, PAGE_TEXT } from '../../../lang';
import {
  Alert,
  Button,
  Dialog,
  Loading,
  Select,
  type HeadCell,
  Table,
  type AlertDetails,
  IconButton,
  NotificationText,
  SwitchButton,
  TextField,
} from '../../../../../components';
import {
  type PrioritizeEvaluationData,
  useGetScholarshipPriorityListPageData,
  useUpdateScholarshipOrdering,
  useSubmitFundingOrder,
} from '../../../graphql/DRCAssessment/PrioritizeEvaluation';
import { FundingRound, FundingSource } from '../../../../Nomination/components';
import { checkInvalidDate } from '../../../../Application/graphql';
import { handleNumberKeyDown } from '../../../../../utils';

type PrioritizeEvaluationProps = {
  viewOnly?: boolean;
  filter?: any;
  department?: string; // Only used for GS to get funding ordering
};

export const PrioritizeEvaluation: React.FunctionComponent<
  PrioritizeEvaluationProps
> = (props: PrioritizeEvaluationProps) => {
  const { viewOnly = true, department = '' } = props;
  const [openAlert, setOpenAlert] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState<{
    content: string;
    handleDialogConfirmAction?: () => void;
  }>({
    content: '',
    handleDialogConfirmAction: undefined,
  });
  const [priorityMode, setPriorityMode] = useState(false);
  let nextEditMode = ''; // for recording the previous edit mode
  const [priorityModeType, setPriorityModeType] = useState(
    PRIORITIZE_EDIT_MODE.INPUT_MODE,
  );

  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const [currentFunding, setCurrentFunding] = useState<FundingSource>(
    FundingSource.POLYU_PRESIDENTIAL_PHD_FELLOWSHIP_SCHEME,
  );
  const [currentRound, setCurrentRound] = useState<FundingRound>(
    FundingRound.FIRST,
  );

  const handleChangeFilterButton =
    (value: FundingSource, round: FundingRound) => () => {
      setCurrentFunding(value);
      setCurrentRound(round);
    };

  const {
    prioritizeEvaluationData,
    priorityFundingInfo,
    loading,
    error,
    refetch,
  } = useGetScholarshipPriorityListPageData(
    '2025', // TODO add academic year filter
    currentRound,
    currentFunding,
    department,
  );

  const buttonFilters = [
    [
      {
        label: 'PPPFS',
        value: FundingSource.POLYU_PRESIDENTIAL_PHD_FELLOWSHIP_SCHEME,
        round: FundingRound.FIRST,
      },
      {
        label: 'IPFS',
        value: FundingSource.INTERNATIONAL_PHD_FELLOWSHIP_SCHEME,
        round: FundingRound.FIRST,
      },
      {
        label: 'Dual PhD Degree Programmes',
        value: FundingSource.DUAL_PHD_DEGREE_PROGRAMME,
        round: FundingRound.FIRST,
      },
      {
        label: 'PolyU Competitive PhD Funding',
        value: FundingSource.POLYU_COMPETITIVE_PHD_FELLOWSHIP_SCHEME,
        round: FundingRound.FIRST,
      },
    ],
    [
      {
        label: 'PPPFS (2nd)',
        value: FundingSource.POLYU_PRESIDENTIAL_PHD_FELLOWSHIP_SCHEME,
        round: FundingRound.SECOND,
      },
      {
        label: 'IPFS (2nd)',
        value: FundingSource.INTERNATIONAL_PHD_FELLOWSHIP_SCHEME,
        round: FundingRound.SECOND,
      },
    ],
  ];

  const displayDeadline = priorityFundingInfo?.deadline;
  const today = new Date();

  const isValidFunding = dayjs(today).isBefore(displayDeadline); // the funding is not expired
  const displayDeadlineText = checkInvalidDate(displayDeadline)
    ? ''
    : isValidFunding
      ? PAGE_TEXT.PRIORITIZE_EVALUATION.SUBMIT_DEADLINE +
        dayjs(displayDeadline).format('DD/MM/YYYY')
      : PAGE_TEXT.PRIORITIZE_EVALUATION.FUNDING_HAS_EXPIRED;

  const hasFundingSubmitted = priorityFundingInfo?.submitted ?? false;

  const enableEditMode =
    checkInvalidDate(displayDeadline) ||
    (isValidFunding && !hasFundingSubmitted);
  const updateScholarshipOrdering = useUpdateScholarshipOrdering();
  const handleUpdateScholarshipOrdering = async (values: {
    prioritizeEvaluationData: PrioritizeEvaluationData[];
  }): Promise<void> => {
    let priorityEvaluationSort = new Array(...values.prioritizeEvaluationData);
    // Check if the orders are in sequence in input mode
    if (inputEditMode) {
      priorityEvaluationSort = priorityEvaluationSort
        .sort((a: any, b: any) => a.order - b.order)
        .map((row, index) => ({ ...row, order: Number(row.order), index }));

      let outSortedIndex = 0;
      const isInSequence = priorityEvaluationSort.every((row: any, index) => {
        const consistence = Number(row?.order) === index + 1;
        if (!consistence) outSortedIndex = index + 1;
        return consistence;
      });

      if (!isInSequence) {
        setAlertDetails({
          alertContent: `The order needs to be sequential, but sequence ${outSortedIndex} is missing. Please correct it and try again. `,
          alertType: 'warning',
        });
        setOpenAlert(true);
        return;
      }
    } else {
      priorityEvaluationSort = priorityEvaluationSort.map((row, index) => ({
        ...row,
        order: index + 1,
        index,
      }));
    }

    const entries = priorityEvaluationSort.map(row => ({
      applicationId: row.applicationId,
      order: Number(row.order),
    }));
    try {
      await updateScholarshipOrdering({
        variables: {
          input: {
            entries,
            funding: currentFunding,
          },
        },
      });

      refetch();
      setAlertDetails({
        alertContent: 'Ordering has been updated. ',
        alertType: 'success',
      });
      await tableData.setFieldValue(
        'prioritizeEvaluationData',
        priorityEvaluationSort,
      );
    } catch (error) {
      setAlertDetails({
        alertContent: 'Error Occurs.' + (error as Error)?.message,
        alertType: 'error',
      });
    }
    setOpenAlert(true);
  };

  const submitScholarshipOrdering = useSubmitFundingOrder();
  const handleSubmitScholarshipOrdering = async (): Promise<void> => {
    setDialogOpen(false);
    try {
      await submitScholarshipOrdering({
        variables: {
          input: {
            funding: currentFunding,
            submitted: true,
          },
        },
      });
      refetch();
      setAlertDetails({
        alertContent: 'Ordering has been submitted. ',
        alertType: 'success',
      });
    } catch (error) {
      setAlertDetails({
        alertContent: 'Error Occurs.' + (error as Error)?.message,
        alertType: 'error',
      });
    }
    setOpenAlert(true);
  };

  const tableData = useFormik({
    initialValues: {
      prioritizeEvaluationData,
    },
    validationSchema: object().shape({
      prioritizeEvaluationData: array().of(
        object().shape({
          order: string()
            .when((_, schema) => {
              return inputEditMode
                ? schema.required('Required')
                : schema.notRequired();
            })
            .test('unique', 'Not unique', function (value) {
              // Get the full list of orders
              const { options } = this;

              const orders =
                options.context?.prioritizeEvaluationData?.map(
                  (item: any) => item.order,
                ) ?? [];
              // Check for duplicates
              const duplicateCount = inputEditMode
                ? orders.filter((order: any) => Number(order) === Number(value))
                    .length
                : 1;

              // Return false if there are more than 1 of the same order
              return duplicateCount === 1;
            }),
        }),
      ),
    }),
    onSubmit: handleUpdateScholarshipOrdering,
  });

  useEffect(() => {
    if (!loading) {
      tableData.resetForm({
        values: {
          prioritizeEvaluationData,
        },
      });
    }
  }, [loading]);

  const hasDataChanged = useMemo(() => {
    let dataChanged = false;
    prioritizeEvaluationData.forEach((row, index) => {
      const hasRowChanged =
        row?.applicationId !==
        tableData.values.prioritizeEvaluationData?.[index]?.applicationId;
      const hasOrderChanged =
        row?.order !==
        tableData.values.prioritizeEvaluationData?.[index]?.order;
      if (hasRowChanged || hasOrderChanged) {
        dataChanged = true;
      }
    });
    return dataChanged;
  }, [prioritizeEvaluationData, tableData]);

  // Reminder before closing or reloading the page
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent): any => {
      if (hasDataChanged) {
        event.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasDataChanged]);

  const handleConfirmSwitchWithoutSave = (): void => {
    setPriorityMode(!priorityMode);
    setDialogOpen(false);
    if (priorityMode) {
      tableData.resetForm({
        values: {
          prioritizeEvaluationData,
        },
      });
    }
  };

  const handleViewApplication =
    (applicationId: string): (() => void) =>
    () => {
      if (applicationId) window.open(`/internal/monitor/${applicationId}`);
    };

  const action = (row: PrioritizeEvaluationData): React.ReactNode => {
    const { applicationId } = row;

    return (
      <div className="flex justify-center items-center">
        <IconButton
          onClick={handleViewApplication(applicationId)}
          color="primary"
          icon={<Visibility />}
        />
      </div>
    );
  };

  const handleInputOrder =
    (row: PrioritizeEvaluationData) =>
    async (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ): Promise<void> => {
      const order = e.target.value;
      await tableData.setFieldValue(
        `prioritizeEvaluationData.${row.index}.order`,
        order,
      );
    };

  const inputEditMode =
    priorityMode && priorityModeType === PRIORITIZE_EDIT_MODE.INPUT_MODE;
  const dragEditMode =
    priorityMode && priorityModeType === PRIORITIZE_EDIT_MODE.DRAG_MODE;

  const prioritizeEvaluationError = tableData.errors
    .prioritizeEvaluationData as
    | Array<FormikErrors<PrioritizeEvaluationData>>
    | undefined;

  const dataCells: Array<HeadCell<PrioritizeEvaluationData>> = [
    ...((!viewOnly
      ? [
          {
            id: 'dragHandler',
            label: '',
            isSortable: false,
            render: (row: PrioritizeEvaluationData) => (
              <div className="overflow-hidden">
                <Reorder
                  fontSize="small"
                  sx={{
                    color: dragEditMode ? '#951D1C' : '#C1C1C1',
                  }}
                />
              </div>
            ),
            minWidth: 5,
          },
        ]
      : []) as Array<HeadCell<PrioritizeEvaluationData>>),
    {
      id: 'order',
      label: MONITOR_LIST_TEXT.ORDER,
      isSortable: false,
      render: (row: PrioritizeEvaluationData) => (
        <div className="overflow-hidden">
          {inputEditMode ? (
            <TextField
              value={row.order}
              type="number"
              size="small"
              name={`prioritizeEvaluationData.${row.index}.order`}
              sx={{
                height: '3rem', // preventing helper text to push it up
                margin: '1rem 0',
              }}
              inputProps={{
                step: 1,
                pattern: '[0-9]*',
              }}
              onChange={handleInputOrder(row)}
              onKeyDown={handleNumberKeyDown}
              onBlur={tableData.handleBlur}
              error={
                tableData.touched?.prioritizeEvaluationData?.[row.index]
                  ?.order &&
                Boolean(prioritizeEvaluationError?.[row.index]?.order)
              }
              helperText={
                tableData.touched?.prioritizeEvaluationData?.[row.index]
                  ?.order && prioritizeEvaluationError?.[row.index]?.order
              }
            />
          ) : (
            row.order
          )}
        </div>
      ),
      minWidth: inputEditMode ? 12 : 5,
    },
    {
      id: 'applicationNo',
      label: MONITOR_LIST_TEXT.APPLICATION_NO,
      isSortable: false,
      render: (row: PrioritizeEvaluationData) => row.applicationNo,
    },
    {
      id: 'applicantName',
      label: MONITOR_LIST_TEXT.APPLICANT_NAME,
      isSortable: false,
      render: (row: PrioritizeEvaluationData) => row.applicantName,
      minWidth: 12,
    },
    {
      id: 'department',
      label: MONITOR_LIST_TEXT.DEPARTMENT,
      isSortable: false,
      render: (row: PrioritizeEvaluationData) => row.department,
    },
    {
      id: 'entryYear',
      label: MONITOR_LIST_TEXT.ENTRY_YEAR,
      isSortable: false,
      render: (row: PrioritizeEvaluationData) => row.entryYear,
    },
    {
      id: 'chiefSupervisor',
      label: MONITOR_LIST_TEXT.CHIEF_SUPERVISOR,
      isSortable: false,
      render: (row: PrioritizeEvaluationData) => row.chiefSupervisor,
    },
    {
      id: 'progType',
      label: MONITOR_LIST_TEXT.PROG_TYPE,
      isSortable: false,
      render: (row: PrioritizeEvaluationData) => row.progType,
    },
    {
      id: 'fundingScheme',
      label: MONITOR_LIST_TEXT.FUNDING_SCHEME,
      isSortable: false,
      minWidth: 18,
      render: (row: PrioritizeEvaluationData) => row.fundingScheme,
    },
    {
      id: 'subjectClassification',
      label: MONITOR_LIST_TEXT.PANEL_TYPE,
      isSortable: false,
      minWidth: 6,
      render: (row: PrioritizeEvaluationData) => row.subjectClassification,
    },
    {
      id: 'fundingScore',
      label: MONITOR_LIST_TEXT.FUNDING_SCORE,
      isSortable: false,
      minWidth: 10,
      render: (row: PrioritizeEvaluationData) => row.fundingScore,
    },
    {
      id: 'action',
      label: MONITOR_LIST_TEXT.ACTION,
      isSortable: false,
      minWidth: 6,
      render: (row: PrioritizeEvaluationData) => action(row),
    },
  ];

  const handleOpenPriorityChangeDialog = (): void => {
    if (hasDataChanged) {
      setDialogContent(priorityDialogContent.clickPriority);
      setDialogOpen(true);
      return;
    }
    setPriorityMode(!priorityMode);
  };

  const handleOpenConfirmSubmitDialog = (): void => {
    // check if all the applications have been prioritized
    const isFinishPrioritized = tableData.values.prioritizeEvaluationData.every(
      (row, index) => Number(row?.order) === index + 1,
    );
    if (!isFinishPrioritized) {
      setAlertDetails({
        alertContent:
          'Please switch on “Edit” and prioritise the nominations, then “Save Current Change” before submission.',
        alertType: 'warning',
      });
      setOpenAlert(true);
      return;
    }
    setDialogContent(priorityDialogContent.submitGS);
    setDialogOpen(true);
  };

  const handleDragRow = async (
    data: PrioritizeEvaluationData[],
  ): Promise<void> => {
    // Do not update order when dragging, it will be finally updated when saving
    await tableData.setFieldValue(
      'prioritizeEvaluationData',
      data.map(row => row),
    );
  };

  const handleConfirmChangeModeWithoutSave = (): void => {
    setDialogOpen(false);
    setPriorityModeType(nextEditMode as PRIORITIZE_EDIT_MODE);
    tableData.resetForm({
      values: {
        prioritizeEvaluationData,
      },
    });
  };

  const handleChangePriorityMode = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    nextEditMode = event.target.value as PRIORITIZE_EDIT_MODE;
    if (hasDataChanged) {
      setDialogContent(priorityDialogContent.changeEditMode);
      setDialogOpen(true);
      return;
    }
    setPriorityModeType(event.target.value as PRIORITIZE_EDIT_MODE);
  };

  const priorityDialogContent = {
    clickPriority: {
      content:
        PAGE_TEXT.PRIORITIZE_EVALUATION.BLOCKER_HELPER_TEXT
          .CANCEL_PRIORITY_MODE,
      handleDialogConfirmAction: handleConfirmSwitchWithoutSave,
    },
    changeEditMode: {
      content:
        PAGE_TEXT.PRIORITIZE_EVALUATION.BLOCKER_HELPER_TEXT.CHANGE_EDIT_MODE,
      handleDialogConfirmAction: handleConfirmChangeModeWithoutSave,
    },
    leavePage: {
      content: PAGE_TEXT.PRIORITIZE_EVALUATION.BLOCKER_HELPER_TEXT.LEAVE_PAGE,
      handleDialogConfirmAction: undefined, // TODO: changing route to trigger
    },
    submitGS: {
      content: PAGE_TEXT.PRIORITIZE_EVALUATION.SUBMIT_GS_CONFIRM,
      handleDialogConfirmAction: handleSubmitScholarshipOrdering,
    },
  };

  if (error)
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: 'Error Occurs.' + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  return (
    <div>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <Dialog
        openDialog={dialogOpen}
        dialogTitle={PAGE_TEXT.PRIORITIZE_EVALUATION.NOTIFICATION}
        dialogAction={PAGE_TEXT.YES}
        cancelAction={PAGE_TEXT.CANCEL}
        onDialogAction={dialogContent.handleDialogConfirmAction}
        onDialogClose={() => {
          setDialogOpen(false);
        }}
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {dialogContent.content}
        </Typography>
      </Dialog>
      {loading && <Loading />}
      <>
        {!viewOnly && (
          <>
            <NotificationText
              note={PAGE_TEXT.PRIORITIZE_EVALUATION.HELPER_TEXT}
              backgroundColor="#EBE0E0"
            />
            <div className="flex flex-row mb-4 mt-4 items-center flex-wrap">
              <span className="text-black font-medium ml-2 italic">
                {PAGE_TEXT.PRIORITIZE_EVALUATION.TIPS}
              </span>
            </div>
          </>
        )}

        <div className="flex flex-row mb-4 mt-4 items-center flex-wrap">
          <div className="flex flex-col">
            {buttonFilters.map((buttonGroup, index) => (
              <div key={index} className="flex mb-2">
                {buttonGroup.map(buttonFilter => {
                  return (
                    <div key={index} className="mr-4">
                      <Button
                        disabled={
                          currentFunding !== buttonFilter.value &&
                          currentRound !== buttonFilter.round &&
                          priorityMode
                        }
                        variant={
                          currentFunding === buttonFilter.value &&
                          currentRound === buttonFilter.round
                            ? 'contained'
                            : 'outlined'
                        }
                        sx={{
                          textTransform: 'none',
                        }}
                        onClick={handleChangeFilterButton(
                          buttonFilter.value,
                          buttonFilter.round,
                        )}
                      >
                        {buttonFilter.label}
                      </Button>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          {!viewOnly && (
            <span className="text-[#951D1C] font-medium ml-2 italic">
              {displayDeadlineText}
            </span>
          )}
        </div>
        <form onSubmit={tableData.handleSubmit} noValidate>
          <>
            {!viewOnly && (
              <div className="flex flex-row mb-2 mt-2 items-center">
                <span className="mr-4 pt-2 pb-2">
                  <span className="text-[#951D1C] font-medium mr-2">
                    {PAGE_TEXT.PRIORITIZE_EVALUATION.EDIT}
                  </span>
                  <SwitchButton
                    checked={priorityMode}
                    onChange={handleOpenPriorityChangeDialog}
                    disabled={!enableEditMode}
                  />
                </span>
                {priorityMode && (
                  <>
                    <span className="text-[#951D1C] font-medium mr-2">
                      {PAGE_TEXT.PRIORITIZE_EVALUATION.PRIORITY_MODE}
                    </span>
                    <Select
                      options={prioritizeModeOptions}
                      value={priorityModeType}
                      size="small"
                      sx={{
                        width: '10rem',
                        marginRight: '1rem',
                      }}
                      onChange={handleChangePriorityMode}
                    />
                    <Button
                      sx={{
                        textTransform: 'none',
                        height: '2rem',
                        borderRadius: '0.5rem',
                      }}
                      variant="contained"
                      type="submit"
                    >
                      {PAGE_TEXT.PRIORITIZE_EVALUATION.SAVE_CURRENT_CHANGE}
                    </Button>
                  </>
                )}
              </div>
            )}

            <div className="w-full flex flex-col items-center mt-4 mb-4">
              {!viewOnly && (
                <Button
                  variant="contained"
                  disabled={
                    priorityMode || hasFundingSubmitted || !isValidFunding
                  }
                  sx={{
                    textTransform: 'none',
                    marginBottom: '0.5rem',
                  }}
                  onClick={handleOpenConfirmSubmitDialog}
                >
                  {PAGE_TEXT.PRIORITIZE_EVALUATION.SUBMIT_ORDER}
                </Button>
              )}

              {hasFundingSubmitted && (
                <span className="text-[#B6B6B6] ml-2 italic flex flex-row items-center text-sm">
                  {`${PAGE_TEXT.PRIORITIZE_EVALUATION.SUBMISSION_TIME} ${priorityFundingInfo?.submittedAt ?? 'N/A'}`}
                </span>
              )}
            </div>
          </>

          <Table<PrioritizeEvaluationData>
            headCells={dataCells}
            data={tableData.values.prioritizeEvaluationData}
            handleRequestSort={() => {}}
            handleDragRow={dragEditMode ? handleDragRow : undefined}
          />
        </form>
      </>
    </div>
  );
};
